import { useEffect, useState } from "react";
import i18n from "../../language/i18n";
import {
  LOADING_TIP_KEYS,
  LoadingContext,
  resolveLoadingLineKey,
  resolveLoadingStage,
  resolveLoadingTip,
} from "../../utilization/LoadingCopyUtilization";
import BaseLoading from "./BaseLoading";

// The mark + a status line that escalates with the wait (5s / 15s / 30s), and from 5s a
// "Biliyor muydunuz?" line under it at medium and up. Pass `context` when the screen knows what it
// is loading, or `text` to word the first line yourself. Copy and timing live in
// LoadingCopyUtilization; CSS in loadingStyles (`ld-`).

interface IBaseLoadingWithTextProps {
  height?: number;
  width?: number;
  size?: string;
  primaryColor?: string;
  secondaryColor?: string;
  hideText?: boolean;
  /** Replaces the first status line — for a wait the screen can describe exactly. */
  text?: string;
  context?: LoadingContext;
  variant?: "mark" | "wordmark";
}

const BaseLoadingWithText = (props: IBaseLoadingWithTextProps) => {
  const {
    height,
    width,
    size,
    primaryColor,
    secondaryColor,
    hideText,
    text,
    context,
    variant,
  } = props;
  const [initialTimestamp] = useState(new Date().getTime());
  const [currentTimestamp, setCurrentTimeStamp] = useState(
    new Date().getTime()
  );
  const [tipOffset] = useState(() =>
    Math.floor(Math.random() * LOADING_TIP_KEYS.length)
  );

  useEffect(() => {
    const timerId = setInterval(() => {
      setCurrentTimeStamp(new Date().getTime());
    }, 1000);
    return () => clearInterval(timerId);
  }, []);

  const elapsed = currentTimestamp - initialTimestamp;
  const stage = resolveLoadingStage(elapsed);

  let textClassName = "";
  let textWidth = 0;
  if (size === "small") {
    textClassName = "ld-txt-sm";
    textWidth = 40;
  } else if (size === "medium") {
    textClassName = "ld-txt-md";
    textWidth = 150;
  } else if (size === "large") {
    textClassName = "ld-txt-base";
    textWidth = 200;
  } else if (size === "xlarge") {
    textClassName = "ld-txt-lg";
    textWidth = 250;
  }

  const showText = !hideText && size !== "small";
  const tip = showText ? resolveLoadingTip(elapsed, tipOffset) : null;

  return (
    <div className="ld-text-wrap" {...(showText ? { role: "status" } : {})}>
      <BaseLoading
        height={height}
        width={width}
        size={size}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
        decorative={showText}
        variant={variant}
      />
      {showText ? (
        <div
          className={`${textClassName} ld-center`}
          style={{ width: textWidth }}
        >
          <p
            key={stage}
            className={`ld-text${stage === 3 ? " ld-text-overdue" : ""}`}
          >
            {stage === 0 && text
              ? text
              : i18n.t(resolveLoadingLineKey(stage, context))}
          </p>
        </div>
      ) : null}
      {tip ? (
        // A sibling of the status box, not a child: it is wider than the narrow sizes' text column
        // and must centre under the mark. Not announced — screen readers hear the status line only.
        <p
          key={tip[1]}
          className="ld-tip"
          aria-hidden="true"
          style={{ width: Math.max(textWidth, 240) }}
        >
          <b>{i18n.t(tip[0])}</b> {i18n.t(tip[1])}
        </p>
      ) : null}
    </div>
  );
};

export default BaseLoadingWithText;
